import React, { useEffect, useState, useMemo } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Bot, Play, Zap, MessageCircle, AlertTriangle, CheckCircle2, Clock,
  Sparkles, Settings, Users, Building2, RefreshCw, ChevronRight, Send,
} from 'lucide-react';

const SEGMENT_COLORS = {
  not_visited: 'bg-amber-100 text-amber-800 border-amber-200',
  visited_no_sale: 'bg-sky-100 text-sky-800 border-sky-200',
  on_trial: 'bg-purple-100 text-purple-800 border-purple-200',
  follow_up: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  post_sale: 'bg-rose-100 text-rose-800 border-rose-200',
};

export default function AgentsTab() {
  const [tab, setTab] = useState('overview');
  const [clinics, setClinics] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [currentCfg, setCurrentCfg] = useState(null);
  const [playbooks, setPlaybooks] = useState([]);
  const [segments, setSegments] = useState({});
  const [runs, setRuns] = useState([]);
  const [handoffs, setHandoffs] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [schedulerRunning, setSchedulerRunning] = useState(false);

  const loadAll = async () => {
    try {
      const [c, cfg, pb, s, r, h, k, sch] = await Promise.all([
        api.get('/clinics'),
        api.get('/agent/configs'),
        api.get('/agent/playbooks'),
        api.get('/agent/segments'),
        api.get('/agent/runs?limit=25'),
        api.get('/agent/handoffs?status=open'),
        api.get('/agent/kpi'),
        api.get('/agent/scheduler'),
      ]);
      setClinics(c.data);
      setConfigs(cfg.data.data || []);
      setPlaybooks(pb.data.data || []);
      setSegments(s.data.labels || {});
      setRuns(r.data.data || []);
      setHandoffs(h.data.data || []);
      setKpi(k.data);
      setScheduler(sch.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load'); }
  };
  useEffect(() => { loadAll(); }, []);

  const toggleScheduler = async (enabled) => {
    try {
      const r = await api.post('/agent/scheduler', { enabled });
      setScheduler(r.data);
      toast.success(enabled ? 'Auto-pilot ON — ticking every ' + r.data.interval_minutes + 'min' : 'Auto-pilot OFF');
    } catch { toast.error('Failed to update scheduler'); }
  };
  const updateSchedulerInterval = async (interval_minutes) => {
    try {
      const r = await api.post('/agent/scheduler', { interval_minutes });
      setScheduler(r.data);
    } catch { toast.error('Failed to update interval'); }
  };
  const runSchedulerNow = async () => {
    setSchedulerRunning(true);
    try {
      const r = await api.post('/agent/scheduler/run-now', {});
      setScheduler(r.data);
      const st = r.data.last_stats;
      toast.success(`Full tick complete · ${st?.clinics || 0} clinics · sent ${st?.sent} · dry ${st?.dry} · handoff ${st?.handoff}`);
      loadAll();
    } catch { toast.error('Scheduler run-now failed'); }
    finally { setSchedulerRunning(false); }
  };

  const activatedCount = configs.filter(c => c.enabled).length;
  const liveCount = configs.filter(c => c.enabled && c.mode === 'live').length;
  const cfgByClinic = useMemo(() =>
    Object.fromEntries(configs.map(c => [c.clinic_name || c.clinic_id, c])), [configs]);

  const openClinic = async (clinic) => {
    setSelectedClinic(clinic);
    try {
      const r = await api.get('/agent/config', { params: { clinic_name: clinic.name } });
      setCurrentCfg(r.data);
      setTab('clinic');
    } catch { toast.error('Failed to load clinic config'); }
  };

  const saveConfig = async (patch) => {
    try {
      const payload = { clinic_name: selectedClinic.name, ...patch };
      const r = await api.post('/agent/config', payload);
      setCurrentCfg(r.data);
      toast.success('Saved');
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const runAgent = async (opts = {}) => {
    setRunning(true); setRunResult(null);
    try {
      const payload = {
        clinic_name: selectedClinic?.name,
        limit_per_segment: opts.limit || 5,
      };
      const r = await api.post('/agent/run', payload);
      setRunResult(r.data);
      toast.success(`Agent ran · sent=${r.data.stats.sent} dry=${r.data.stats.dry} handoff=${r.data.stats.handoff}`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Run failed'); }
    finally { setRunning(false); }
  };

  return (
    <div className="space-y-4" data-testid="agents-tab">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Bot className="w-5 h-5 text-indigo-700" />
            <h2 className="font-heading text-xl font-bold text-stone-900">AI Clinic Agents</h2>
            <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 text-[10px]">Claude Sonnet 4.6</Badge>
          </div>
          <p className="text-xs text-stone-500">
            Per-clinic autonomous agent working 5 segments: no-show reactivation, post-visit report + review, on-trial check-ins,
            follow-up negotiation, and post-sale onboarding. Dry-run by default; flip to Live per clinic when confident.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadAll} data-testid="agents-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KPI label="Clinics enabled" value={activatedCount} icon={Building2} tone="info" tid="kpi-clinics-enabled" />
        <KPI label="Live" value={liveCount} icon={Zap} tone="success" tid="kpi-live" />
        <KPI label="Messages sent" value={kpi?.sent} icon={Send} tone="success" tid="kpi-sent" />
        <KPI label="Handoffs open" value={kpi?.open_handoffs} icon={AlertTriangle} tone="warn" tid="kpi-handoffs" />
        <KPI label="Runs (recent)" value={kpi?.runs} icon={Clock} tid="kpi-runs" />
      </div>

      {/* Auto-pilot scheduler card */}
      <Card className={`p-3 border ${scheduler?.enabled ? 'border-emerald-200 bg-emerald-50' : 'border-stone-200 bg-white'}`}
        data-testid="agents-scheduler-card">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${scheduler?.enabled ? 'bg-emerald-100' : 'bg-stone-100'}`}>
              <Bot className={`w-4 h-4 ${scheduler?.enabled ? 'text-emerald-700' : 'text-stone-500'}`} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500">Auto-pilot</div>
              <div className="text-sm font-semibold text-stone-900">
                {scheduler?.enabled
                  ? `ON · every ${scheduler.interval_minutes}min · ${scheduler.limit_per_segment}/seg`
                  : 'OFF · manual runs only'}
              </div>
              {scheduler?.last_tick && (
                <div className="text-[11px] text-stone-500">
                  Last tick: {formatWhen(scheduler.last_tick)} · {scheduler.last_stats?.clinics || 0} clinics ·
                  sent {scheduler.last_stats?.sent || 0} · dry {scheduler.last_stats?.dry || 0} ·
                  handoff {scheduler.last_stats?.handoff || 0}
                </div>
              )}
              {scheduler?.next_run_at && scheduler.enabled && (
                <div className="text-[11px] text-emerald-700">Next tick: {formatWhen(scheduler.next_run_at)}</div>
              )}
            </div>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <Label className="text-xs">Interval</Label>
            <Select value={String(scheduler?.interval_minutes || 60)} onValueChange={(v) => updateSchedulerInterval(Number(v))}>
              <SelectTrigger className="h-8 w-28 text-xs" data-testid="agents-sched-interval"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 min</SelectItem>
                <SelectItem value="30">30 min</SelectItem>
                <SelectItem value="60">1 hr</SelectItem>
                <SelectItem value="180">3 hrs</SelectItem>
                <SelectItem value="360">6 hrs</SelectItem>
                <SelectItem value="720">12 hrs</SelectItem>
                <SelectItem value="1440">1 day</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-8" onClick={runSchedulerNow} disabled={schedulerRunning} data-testid="agents-sched-run-now">
              {schedulerRunning ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
              Run all now
            </Button>
            <Label className="text-xs ml-2">Auto</Label>
            <Switch checked={!!scheduler?.enabled} onCheckedChange={toggleScheduler} data-testid="agents-sched-toggle" />
          </div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-4 w-full h-9">
          <TabsTrigger value="overview" data-testid="agents-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="clinic" data-testid="agents-tab-clinic">Clinic config</TabsTrigger>
          <TabsTrigger value="playbooks" data-testid="agents-tab-playbooks">Playbooks ({playbooks.length})</TabsTrigger>
          <TabsTrigger value="runs" data-testid="agents-tab-runs">Runs & Handoffs</TabsTrigger>
        </TabsList>

        {/* OVERVIEW: clinics grid */}
        <TabsContent value="overview" className="mt-3">
          <Card className="border-stone-200">
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-xs">
                <thead className="bg-stone-50 sticky top-0">
                  <tr>
                    {['Clinic', 'Status', 'Mode', 'Segments', 'Working hrs', 'Action'].map(h => (
                      <th key={h} className="text-left px-3 py-2 font-semibold text-stone-600 uppercase tracking-wider text-[10px]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {clinics.map(c => {
                    const cfg = cfgByClinic[c.name] || {};
                    return (
                      <tr key={c.id} className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer"
                          onClick={() => openClinic(c)} data-testid={`agents-clinic-${c.id}`}>
                        <td className="px-3 py-2 font-medium">{c.name}</td>
                        <td className="px-3 py-2">
                          {cfg.enabled
                            ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Enabled</Badge>
                            : <Badge className="bg-stone-100 text-stone-600">Disabled</Badge>}
                        </td>
                        <td className="px-3 py-2">
                          {cfg.enabled && (
                            cfg.mode === 'live'
                              ? <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px] uppercase">Live</Badge>
                              : <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] uppercase">Dry-run</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(cfg.segments_enabled || []).map(s => (
                              <span key={s} className={`text-[9px] px-1.5 py-0.5 rounded border ${SEGMENT_COLORS[s]}`}>
                                {s.replace('_', ' ')}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-stone-500 tabular-nums font-mono text-[11px]">
                          {cfg.working_start ?? 9}:00 – {cfg.working_end ?? 21}:00 IST
                        </td>
                        <td className="px-3 py-2">
                          <ChevronRight className="w-4 h-4 text-stone-400" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* CLINIC CONFIG */}
        <TabsContent value="clinic" className="mt-3">
          {!selectedClinic ? (
            <Card className="p-6 border-dashed border-2 border-stone-200 text-center">
              <Bot className="w-8 h-8 text-stone-300 mx-auto mb-2" />
              <div className="text-sm text-stone-500">Select a clinic from Overview to configure its agent.</div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Card className="p-4 border-stone-200 md:col-span-2 space-y-3" data-testid="agents-config-card">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Clinic</div>
                    <div className="font-heading text-lg font-bold">{selectedClinic.name}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Enable agent</Label>
                    <Switch checked={!!currentCfg?.enabled}
                      onCheckedChange={(v) => saveConfig({ enabled: v })}
                      data-testid="agents-enable-toggle" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-stone-600">Mode</Label>
                    <Select value={currentCfg?.mode || 'dry_run'} onValueChange={(v) => saveConfig({ mode: v })}>
                      <SelectTrigger className="h-9 mt-1" data-testid="agents-mode-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dry_run">Dry-run (compose only, do not send)</SelectItem>
                        <SelectItem value="live">Live (actually send via WhatsApp)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-stone-600">Discount band (%)</Label>
                    <Input type="number" min="0" max="50"
                      value={currentCfg?.discount_band_pct || 0}
                      onChange={(e) => setCurrentCfg({ ...currentCfg, discount_band_pct: Number(e.target.value) })}
                      onBlur={(e) => saveConfig({ discount_band_pct: Number(e.target.value) })}
                      className="h-9 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-600">Working start (IST hour)</Label>
                    <Input type="number" min="0" max="23"
                      value={currentCfg?.working_start ?? 9}
                      onChange={(e) => setCurrentCfg({ ...currentCfg, working_start: Number(e.target.value) })}
                      onBlur={(e) => saveConfig({ working_start: Number(e.target.value) })}
                      className="h-9 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-600">Working end (IST hour)</Label>
                    <Input type="number" min="0" max="24"
                      value={currentCfg?.working_end ?? 21}
                      onChange={(e) => setCurrentCfg({ ...currentCfg, working_end: Number(e.target.value) })}
                      onBlur={(e) => saveConfig({ working_end: Number(e.target.value) })}
                      className="h-9 mt-1" />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-stone-600">Segments enabled</Label>
                  <div className="mt-1 grid grid-cols-2 md:grid-cols-3 gap-1">
                    {Object.entries(segments).map(([k, v]) => {
                      const on = (currentCfg?.segments_enabled || []).includes(k);
                      return (
                        <button key={k} type="button" data-testid={`agents-segment-${k}`}
                          onClick={() => {
                            const cur = currentCfg?.segments_enabled || [];
                            const next = on ? cur.filter(x => x !== k) : [...cur, k];
                            saveConfig({ segments_enabled: next });
                          }}
                          className={`text-left text-xs px-2 py-1.5 rounded border transition-colors ${on ? SEGMENT_COLORS[k] : 'bg-white border-stone-200 hover:bg-stone-50'}`}>
                          <span className="mr-1">{on ? '✓' : '○'}</span>
                          <span className="text-[11px]">{v}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-stone-600">Google Review link</Label>
                    <Input value={currentCfg?.review_link || ''}
                      onChange={(e) => setCurrentCfg({ ...currentCfg, review_link: e.target.value })}
                      onBlur={(e) => saveConfig({ review_link: e.target.value })}
                      placeholder="https://g.page/hearclear-…" className="h-9 mt-1 font-mono text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-600">Report link template</Label>
                    <Input value={currentCfg?.report_link_template || ''}
                      onChange={(e) => setCurrentCfg({ ...currentCfg, report_link_template: e.target.value })}
                      onBlur={(e) => saveConfig({ report_link_template: e.target.value })}
                      placeholder="https://hearclear.in/report/{ticket_id}"
                      className="h-9 mt-1 font-mono text-xs" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Notes</Label>
                  <Textarea value={currentCfg?.notes || ''}
                    onChange={(e) => setCurrentCfg({ ...currentCfg, notes: e.target.value })}
                    onBlur={(e) => saveConfig({ notes: e.target.value })}
                    className="mt-1 text-xs min-h-[60px]" placeholder="Special instructions for this clinic's agent…" />
                </div>
              </Card>

              <Card className="p-4 border-indigo-200 bg-indigo-50/50 space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-semibold">Run agent</div>
                  <div className="text-xs text-stone-600 mt-0.5">Trigger one tick now — will compose (dry-run) or send (live) up to 5 messages per enabled segment.</div>
                </div>
                <Button onClick={() => runAgent({ limit: 5 })} disabled={running || !currentCfg?.enabled}
                  className="w-full bg-indigo-700 hover:bg-indigo-800" data-testid="agents-run-btn">
                  {running ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}
                  Run once ({currentCfg?.mode || 'dry_run'})
                </Button>
                {!currentCfg?.enabled && (
                  <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                    <AlertTriangle className="w-3 h-3 inline mr-1" /> Enable the agent above to run.
                  </div>
                )}
                {runResult && (
                  <div className="text-xs space-y-1 mt-2 border-t border-indigo-200 pt-2">
                    <div className="font-semibold text-indigo-900">Latest run</div>
                    <div>Sent: <b>{runResult.stats.sent}</b> · Dry: <b>{runResult.stats.dry}</b> · Handoff: <b>{runResult.stats.handoff}</b> · Skipped: <b>{runResult.stats.skipped}</b></div>
                    {Object.entries(runResult.stats.per_segment || {}).map(([k, v]) => (
                      <div key={k} className="text-[11px] text-stone-600">
                        · {k}: {v.targets} targets → sent {v.sent} · dry {v.dry} · handoff {v.handoff} · skip {v.skipped}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          )}
        </TabsContent>

        {/* PLAYBOOKS */}
        <TabsContent value="playbooks" className="mt-3 space-y-3">
          {playbooks.map(pb => (
            <PlaybookCard key={pb.id} pb={pb} segmentLabel={segments[pb.segment]}
              onSaved={loadAll} />
          ))}
        </TabsContent>

        {/* RUNS & HANDOFFS */}
        <TabsContent value="runs" className="mt-3 space-y-3">
          <Card className="border-stone-200">
            <div className="px-3 py-2 border-b border-stone-200 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-stone-500" />
              <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                Recent runs ({runs.length})
              </span>
            </div>
            <div className="overflow-x-auto max-h-72">
              <table className="w-full text-xs">
                <thead className="bg-stone-50 sticky top-0">
                  <tr>
                    {['When', 'Clinic', 'Mode', 'Sent', 'Dry', 'Handoff', 'Skipped'].map(h => (
                      <th key={h} className="text-left px-3 py-2 font-semibold text-stone-600 uppercase tracking-wider text-[10px]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {runs.map(r => (
                    <tr key={r.id} className="border-b border-stone-100" data-testid={`agents-run-${r.id}`}>
                      <td className="px-3 py-1.5 tabular-nums text-stone-600">{formatWhen(r.started_at)}</td>
                      <td className="px-3 py-1.5">{r.clinic_name || <span className="text-stone-400">— all —</span>}</td>
                      <td className="px-3 py-1.5">
                        {r.mode === 'live'
                          ? <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px] uppercase">Live</Badge>
                          : <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] uppercase">Dry</Badge>}
                      </td>
                      <td className="px-3 py-1.5 tabular-nums text-emerald-700 font-semibold">{r.stats?.sent || 0}</td>
                      <td className="px-3 py-1.5 tabular-nums">{r.stats?.dry || 0}</td>
                      <td className="px-3 py-1.5 tabular-nums text-amber-700">{r.stats?.handoff || 0}</td>
                      <td className="px-3 py-1.5 tabular-nums text-stone-500">{r.stats?.skipped || 0}</td>
                    </tr>
                  ))}
                  {runs.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-stone-400">No runs yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="border-stone-200">
            <div className="px-3 py-2 border-b border-stone-200 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
              <span className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">
                Open handoffs ({handoffs.length})
              </span>
            </div>
            <div className="overflow-x-auto max-h-72">
              <table className="w-full text-xs">
                <thead className="bg-stone-50 sticky top-0">
                  <tr>
                    {['When', 'Patient', 'Clinic', 'Segment', 'Category', 'Route to', ''].map(h => (
                      <th key={h} className="text-left px-3 py-2 font-semibold text-stone-600 uppercase tracking-wider text-[10px]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {handoffs.map(h => (
                    <tr key={h.id} className="border-b border-stone-100" data-testid={`agents-handoff-${h.id}`}>
                      <td className="px-3 py-1.5 tabular-nums text-stone-600">{formatWhen(h.created_at)}</td>
                      <td className="px-3 py-1.5 font-medium">{h.patient_name}</td>
                      <td className="px-3 py-1.5 text-stone-600">{h.clinic_name}</td>
                      <td className="px-3 py-1.5">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded border ${SEGMENT_COLORS[h.segment]}`}>{h.segment}</span>
                      </td>
                      <td className="px-3 py-1.5 text-stone-500">{h.category || '—'}</td>
                      <td className="px-3 py-1.5 text-stone-500">{h.assigned_to_role || '—'}</td>
                      <td className="px-3 py-1.5">
                        <Button size="sm" variant="ghost" className="h-7 text-[10px]"
                          onClick={() => api.patch(`/agent/handoffs/${h.id}`, { status: 'closed' }).then(loadAll)}
                          data-testid={`handoff-close-${h.id}`}>
                          Close
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {handoffs.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-stone-400">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto mb-1" /> No open handoffs — nice work!
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PlaybookCard({ pb, segmentLabel, onSaved }) {
  const [edit, setEdit] = useState(pb);
  const [dirty, setDirty] = useState(false);
  const save = async () => {
    try {
      await api.patch(`/agent/playbooks/${pb.id}`, {
        name: edit.name, system_prompt: edit.system_prompt,
        message_template: edit.message_template, max_touches: edit.max_touches,
        cooldown_hours: edit.cooldown_hours, template_id: edit.template_id,
        active: edit.active,
      });
      toast.success('Playbook saved'); setDirty(false); onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const set = (k, v) => { setEdit(x => ({ ...x, [k]: v })); setDirty(true); };
  return (
    <Card className="border-stone-200 p-3" data-testid={`agents-playbook-${pb.segment}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-[9px] px-1.5 py-0.5 rounded border ${SEGMENT_COLORS[pb.segment]}`}>{pb.segment}</span>
          <div className="font-heading font-bold text-sm truncate">{edit.name}</div>
          <span className="text-[10px] font-mono text-stone-400">v{pb.version}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Label className="text-[10px]">Active</Label>
          <Switch checked={edit.active} onCheckedChange={(v) => set('active', v)} />
        </div>
      </div>
      <div className="text-[11px] text-stone-500 mt-0.5">{segmentLabel}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold flex items-center gap-1"><Sparkles className="w-3 h-3" /> System prompt (agent brain)</Label>
          <Textarea value={edit.system_prompt || ''} onChange={(e) => set('system_prompt', e.target.value)}
            className="text-[11px] min-h-[100px] mt-1" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold flex items-center gap-1"><MessageCircle className="w-3 h-3" /> Fallback template</Label>
          <Textarea value={edit.message_template || ''} onChange={(e) => set('message_template', e.target.value)}
            className="text-[11px] min-h-[100px] mt-1" />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <Label className="text-[10px]">Max touches / 30d</Label>
            <Input type="number" value={edit.max_touches || 0} onChange={(e) => set('max_touches', Number(e.target.value))} className="h-8 mt-1" />
          </div>
          <div className="flex-1">
            <Label className="text-[10px]">Cooldown hrs</Label>
            <Input type="number" value={edit.cooldown_hours || 0} onChange={(e) => set('cooldown_hours', Number(e.target.value))} className="h-8 mt-1" />
          </div>
          <div className="flex-1">
            <Label className="text-[10px]">WA template</Label>
            <Input value={edit.template_id || ''} onChange={(e) => set('template_id', e.target.value)} className="h-8 mt-1 font-mono text-xs" />
          </div>
        </div>
        <div className="flex items-end justify-end">
          <Button onClick={save} disabled={!dirty} size="sm" className="bg-stone-900 hover:bg-stone-800 text-white h-8">Save</Button>
        </div>
      </div>
    </Card>
  );
}

function KPI({ label, value, icon: Icon, tone = 'default', tid }) {
  const tones = {
    default: 'bg-white border-stone-200 text-stone-800',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    warn: 'bg-amber-50 border-amber-200 text-amber-800',
    info: 'bg-indigo-50 border-indigo-200 text-indigo-800',
  }[tone];
  return (
    <div className={`p-3 border rounded-md ${tones}`} data-testid={tid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest font-semibold opacity-70">{label}</span>
        <Icon className="w-3.5 h-3.5 opacity-60" />
      </div>
      <div className="font-heading font-bold text-xl tabular-nums">
        {value == null ? '—' : value.toLocaleString()}
      </div>
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}
